import { Component, type ErrorInfo, type ReactNode } from 'react'
import { ErrorState } from './ErrorState.js'

export interface ErrorBoundaryProps {
  children: ReactNode
  /** Drawn instead of the default. Receives a way to try again. */
  fallback?: (error: Error, retry: () => void) => ReactNode
  /** Somewhere to send it. The desk has no crash reporter yet; this is the hook. */
  onError?: (error: Error, info: ErrorInfo) => void
  /**
   * Change this and the boundary resets itself.
   *
   * Put the route key here. Without it, one screen throwing leaves the error in
   * place while somebody navigates around it, and the desk appears to be broken
   * everywhere rather than in one place.
   */
  resetKey?: string | number
}

interface State {
  error: Error | null
}

/**
 * Catches a component that throws, so the desk does not go white.
 *
 * `ErrorState` draws the message; **nothing was catching the throw.** A React
 * tree that throws during render unmounts itself, and what is left is a blank
 * page with the real reason in a console nobody on the client's team will open.
 * The specification's *"the unexpected error — the shape of every failure, not a
 * screen to fetch"* needs something to reach it, and this is the only thing that
 * can: React still requires a class for this and provides no hook.
 *
 * Wrapped around the content area rather than the whole app, so the rail and the
 * header survive and there is a way out of the broken screen.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    /* Into the console as well as the callback. There is no crash reporter
       yet, and the component stack is the difference between "a screen
       broke" and knowing which one. */
    console.error('The desk crashed:', error, info.componentStack)
    this.props.onError?.(error, info)
  }

  override componentDidUpdate(previous: ErrorBoundaryProps) {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  private readonly retry = () => this.setState({ error: null })

  override render() {
    const { error } = this.state
    if (!error) return this.props.children
    if (this.props.fallback) return this.props.fallback(error, this.retry)

    /**
     * A sentence they can act on, and the detail they can quote.
     *
     * This drew `error.message` as the whole explanation — which for a React
     * crash is *"Rendered more hooks than during the previous render"*, shown
     * to somebody running a wellness marketplace. It is the right thing to
     * **keep**, because it is the one string that tells us what happened, and
     * the wrong thing to lead with.
     *
     * So: our words first, theirs underneath, and a button that puts the
     * detail on the clipboard — because the alternative is a photograph of a
     * screen, which is what people actually send.
     */
    return (
      <ErrorState
        message="Something on this page went wrong. Nothing has been changed or lost, and the rest of the desk still works."
        detail={error.message}
        onRetry={this.retry}
      />
    )
  }
}
